import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Search, Sparkles, Inbox, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const THREADS_API = `${BACKEND_URL}/api/threads`;
const POLL_MS = 10000;

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const r = await axios.get(THREADS_API);
      setThreads(r.data || []);
      if (!selectedId && r.data && r.data.length) setSelectedId(r.data[0].id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedId]);

  const fetchThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await axios.get(`${THREADS_API}/${id}`);
      setThread(r.data.thread);
      setMessages(r.data.messages || []);
      // mark read
      if (r.data.thread?.unread) {
        axios.post(`${THREADS_API}/${id}/read`).then(fetchThreads).catch(() => {});
      }
    } catch (e) { console.error(e); }
  }, [fetchThreads]);

  // Initial load
  useEffect(() => { fetchThreads(); }, []); // eslint-disable-line

  // Whenever selection changes, fetch full thread
  useEffect(() => { if (selectedId) fetchThread(selectedId); }, [selectedId, fetchThread]);

  // Poll for new threads/messages
  useEffect(() => {
    const id = setInterval(() => {
      fetchThreads();
      if (selectedId) fetchThread(selectedId);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [selectedId, fetchThreads, fetchThread]);

  // Wingman can drop a draft into the composer
  useEffect(() => {
    const onDraft = (e) => {
      const { threadId, body } = e?.detail || {};
      if (body) setText(body);
      // 'current' magic value means "use whatever's selected" — don't reassign selection
      if (threadId && threadId !== 'current') setSelectedId(threadId);
    };
    window.addEventListener('wingman:draftReply', onDraft);
    return () => window.removeEventListener('wingman:draftReply', onDraft);
  }, []);

  // Auto-scroll messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, selectedId]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || !selectedId || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${THREADS_API}/${selectedId}/messages`, {
        body, author: 'Cody Matthews', role: 'lo',
      });
      setMessages((prev) => [...prev, r.data]);
      setText('');
      fetchThreads();
    } catch (err) {
      toast.error('Failed to send message');
    } finally { setSending(false); }
  };

  const filtered = threads.filter((t) =>
    !query ||
    t.subject.toLowerCase().includes(query.toLowerCase()) ||
    (t.lead_name || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      <div>
        <div className="text-[11.5px] text-neutral-500">Workspace / Messages</div>
        <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Conversations</h1>
        <p className="text-[13px] text-neutral-500">
          <span className="mono font-semibold text-neutral-700">{threads.length}</span> threads ·
          <span className="ml-1 mono font-semibold text-[hsl(var(--kf-gold-deep))]">
            {threads.filter((t) => t.unread).length} unread
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 rounded-2xl border hairline bg-white overflow-hidden min-h-[600px]">
        {/* Thread list */}
        <aside className="lg:col-span-4 border-r hairline flex flex-col">
          <div className="p-3 border-b hairline">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                data-testid="messages-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search threads…"
                className="w-full h-9 pl-9 pr-3 rounded-lg bg-neutral-50 border hairline text-[12.5px] placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 focus:bg-white"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="p-8 flex items-center justify-center text-[12px] text-neutral-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-8 text-center text-[12px] text-neutral-500">
                <Inbox className="h-6 w-6 mx-auto mb-2 text-neutral-300" />
                No threads.
              </div>
            )}
            {filtered.map((t) => {
              const active = selectedId === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`thread-${t.id}`}
                  onClick={() => setSelectedId(t.id)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b hairline-soft hover:bg-neutral-50/70 transition relative',
                    active && 'bg-neutral-50'
                  )}
                >
                  {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-[hsl(var(--kf-gold))]" />}
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-neutral-900 text-white text-[10.5px] font-bold flex items-center justify-center shrink-0">
                      {t.initials || '??'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[12.5px] font-bold text-neutral-900 truncate">{t.lead_name || '—'}</span>
                        <span className="text-[10px] text-neutral-400 shrink-0 mono">{fmtTime(t.last_message_at)}</span>
                      </div>
                      <div className="text-[11.5px] font-semibold text-neutral-800 truncate">{t.subject}</div>
                      <div className="text-[10.5px] text-neutral-500 truncate">{t.last_message_preview}</div>
                    </div>
                    {t.unread && <span className="h-2 w-2 rounded-full bg-[hsl(var(--kf-gold))]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Active conversation */}
        <section className="lg:col-span-8 flex flex-col bg-neutral-50/40">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-[12.5px] text-neutral-400">
              {loading ? 'Loading…' : 'Select a thread to view messages'}
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b hairline bg-white flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Subject</div>
                  <div className="display text-[16px] font-bold text-neutral-900">{thread.subject}</div>
                </div>
                <div className="flex items-center gap-2">
                  {thread.lead_name && (
                    <span className="text-[9.5px] uppercase tracking-wider bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/30 font-bold px-2 py-1 rounded">
                      {thread.lead_name}
                    </span>
                  )}
                  <span className="text-[9.5px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold px-2 py-1 rounded">Active</span>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="thread-messages">
                {messages.map((m) => (
                  <div key={m.id} className={cn('flex', m.mine ? 'justify-end' : 'justify-start')}>
                    <div className={cn(
                      'max-w-[75%] rounded-2xl px-4 py-3 border',
                      m.mine
                        ? 'bg-neutral-900 text-white border-neutral-900 rounded-br-sm'
                        : 'bg-white text-neutral-800 hairline rounded-bl-sm'
                    )}>
                      <div className={cn('text-[10.5px] font-bold uppercase tracking-wider flex items-center gap-1.5',
                        m.mine ? 'text-white/70' : 'text-neutral-500')}>
                        {m.role === 'wingman' && <Sparkles className="h-2.5 w-2.5 text-[hsl(var(--kf-gold))]" />}
                        {m.author} · {m.role}
                      </div>
                      <div className={cn('mt-1 text-[13px] leading-relaxed whitespace-pre-wrap',
                        m.mine ? 'text-white' : 'text-neutral-800')}>
                        {m.body}
                      </div>
                      <div className={cn('mt-1.5 text-[10px] mono', m.mine ? 'text-white/50' : 'text-neutral-400')}>
                        {fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center py-12 text-[12px] text-neutral-400">No messages yet — send the first.</div>
                )}
              </div>

              <form onSubmit={send} className="px-4 py-3 border-t hairline bg-white flex items-end gap-2">
                <textarea
                  data-testid="message-composer"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  rows={1}
                  placeholder="Write a reply… (Wingman can draft this for you)"
                  disabled={sending}
                  className="flex-1 resize-none px-3 py-2 rounded-md bg-neutral-50 border hairline text-[13px] placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 disabled:opacity-50"
                />
                <button
                  data-testid="message-send"
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="h-9 px-3 rounded-md btn-ink text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? 'Sending' : 'Send'}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
